import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import i18n from '../../i18n/index';
import { BLOG_POSTS } from './data';

// Blog index at /blog — lists all posts (newest first) and drives signups.
const BlogIndex = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentLanguage(i18n.language || 'en');
    document.title = 'AI Headshots Blog — Tips, Guides & Comparisons | HeadshotsNow.ai';
  }, []);

  const postHref = (slug) =>
    currentLanguage === 'en' ? `/blog/${slug}` : `/${currentLanguage}/blog/${slug}`;
  const signupHref = currentLanguage === 'en' ? '/signup' : `/${currentLanguage}/signup`;

  const [featured, ...rest] = BLOG_POSTS;

  return (
    <main className="bg-black text-white">
      {/* Hero */}
      <section className="section-top-80 px-[15px]">
        <div className="mx-auto max-w-[900px] text-center pt-16 pb-10">
          <span className="inline-block text-xs font-semibold tracking-wide uppercase text-cyan-400 mb-4">
            The HeadshotsNow blog
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5">
            AI headshot tips, guides &amp; comparisons
          </h1>
          <p className="text-lg text-white/80 max-w-[700px] mx-auto mb-8">
            Everything you need to get professional, profile-ready headshots — how to shoot great
            source photos, how AI compares to a studio, and how to look your best online.
          </p>
          <Link
            to={signupHref}
            className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
          >
            Get your AI headshots
          </Link>
        </div>
      </section>

      {/* Featured post */}
      {featured && (
        <section className="px-[15px] pb-4">
          <div className="mx-auto max-w-[1000px]">
            <Link
              to={postHref(featured.slug)}
              className="block rounded-3xl bg-neutral-900 p-8 transition-colors hover:bg-neutral-800 md:p-10"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-cyan-400">
                Latest
              </span>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold">{featured.title}</h2>
              <p className="mt-2 text-sm text-white/50">
                {featured.date} · {featured.readTime}
              </p>
              <p className="mt-4 text-white/80 max-w-[720px]">{featured.excerpt}</p>
              <span className="mt-5 inline-block font-semibold text-cyan-400">Read article →</span>
            </Link>
          </div>
        </section>
      )}

      {/* Post grid */}
      <section className="px-[15px] py-8">
        <div className="mx-auto max-w-[1000px]">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={postHref(p.slug)}
                className="flex flex-col bg-neutral-900 rounded-2xl p-6 transition-colors hover:bg-neutral-800"
              >
                <h3 className="text-lg font-semibold mb-2">{p.title}</h3>
                <p className="text-sm text-white/60 flex-1">{p.excerpt}</p>
                <p className="mt-4 text-xs text-white/40">
                  {p.date} · {p.readTime}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-[15px] py-12">
        <div className="mx-auto max-w-[800px] text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
          <Link
            to={signupHref}
            className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
          >
            Create my headshots
          </Link>
          <p className="text-xs text-white/50 mt-4">
            Results are AI-generated and may vary with the quality of your uploaded photos.
          </p>
        </div>
      </section>
    </main>
  );
};

export default BlogIndex;
